import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { systemFeatureService } from "../services/systemFeatureService";
import { userPrivilegeKeys, useCurrentUserPrivileges } from "./useUserPrivileges";
import { useBottomNotificationStore } from "../stores/bottomNotificationStore";

export const systemFeatureKeys = {
  all: ["systemFeatures"],
};

export const useSystemFeatures = () => {
  const showNotification = useBottomNotificationStore((s) => s.showNotification);

  return useQuery(
    systemFeatureKeys.all,
    () => systemFeatureService.getAllFeatures(),
    {
      onError: () => {
        showNotification("Failed to fetch system features", "error");
      },
    }
  );
};

/**
 * Returns true when the given feature key is enabled for the current user.
 *
 * Wraps the existing useCurrentUserPrivileges.isSystemFeatureEnabled so that
 * non-super-admin consumers don't have to destructure it from that hook.
 * Fails open while the privilege query is loading so slow networks don't
 * flash a disabled-feature screen.
 */
export const useIsFeatureEnabled = (featureKey) => {
  const { isSystemFeatureEnabled, isLoading } = useCurrentUserPrivileges();
  if (isLoading) return true;
  return isSystemFeatureEnabled(featureKey);
};

export const useSaveSystemFeatures = () => {
  const queryClient = useQueryClient();
  const showNotification = useBottomNotificationStore((s) => s.showNotification);

  return useMutation(
    (data) => systemFeatureService.saveFeatures(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(systemFeatureKeys.all);
        queryClient.invalidateQueries(userPrivilegeKeys.currentUser);
      },
      onError: (error) => {
        showNotification(error.message || "Failed to save system features", "error");
      },
    }
  );
};
